import { safeWorklistItem } from '@features/patient-coverages/utils/patientCost';
import type { WorklistItem } from '@store/generated/eligibility';

import { isLockActive } from './lockState';

/**
 * Visual state for a worklist row, derived from
 * `(is_self_pay, coverage_kind, readiness, lock_expires_at)`. Shared between the row
 * component (for rendering) and the page (for analytics + auto-expand
 * routing) so the FE has one source of truth for "what kind of row is
 * this."
 *
 * Two states distinct from "needs you now" surface throughput-prep work:
 *
 *  - **awaiting_check**: patient has coverage on file but no eligibility
 *    result yet. Front-desk-actionable via per-row "Run" or bulk
 *    "Run all / Run selected".
 *  - **needs_coverage**: patient has no coverage on file. Front-desk
 *    needs to call/ask and add a policy.
 *
 * Both intentionally stay quiet on calendar block badges (they're not
 * urgent unless the appointment is imminent).
 */
export type WorklistRowState =
  | 'blocked'
  | 'attention'
  | 'collect'
  | 'info'
  | 'awaiting_check'
  | 'needs_coverage'
  | 'verified_lock_active'
  | 'verified_lock_expired'
  | 'collected'
  | 'self_pay';

export function deriveWorklistRowState(item: WorklistItem): WorklistRowState {
  // Self-pay comes from the item-level patient flag (GDN-3068), never
  // from coverage_kind: kind describes real coverage rows and is null for
  // self-pay patients once designation stops creating rows. (The old
  // `coverage_kind === 'self-pay'` compare here had been dead since the
  // kind-normalization migration of 2026-05-25 — canonical kinds are
  // underscored.)
  if (item.is_self_pay) return 'self_pay';

  // No coverage on file — front desk needs to add a policy. The BE
  // returns `coverage_kind` as null/undefined or 'none' when there's no
  // active coverage; treat all three as needs_coverage. Distinct from
  // the legacy "blocked" (which now covers "has coverage but can't bill").
  if (item.coverage_kind == null || item.coverage_kind === 'none') {
    return 'needs_coverage';
  }

  switch (safeWorklistItem(item).readiness) {
    case 'blocked':
      return 'blocked';
    case 'attention':
      return 'attention';
    case 'collect':
      return 'collect';
    case 'info':
      return 'info';
    case 'collected':
      return 'collected';
    case 'verified': {
      if (item.lock_expires_at) {
        return isLockActive(item.lock_expires_at)
          ? 'verified_lock_active'
          : 'verified_lock_expired';
      }
      return 'verified_lock_active';
    }
    case null:
    case undefined:
    default:
      return 'awaiting_check';
  }
}

/** Which inline drawer form a worklist row click should auto-expand. */
export type WorklistAutoExpand = 'verification' | 'collection' | null;

const AUTO_EXPAND_BY_STATE: Record<WorklistRowState, WorklistAutoExpand> = {
  blocked: 'verification',
  attention: 'verification',
  collect: 'collection',
  info: null,
  awaiting_check: null,
  needs_coverage: null,
  verified_lock_active: null,
  verified_lock_expired: 'verification',
  collected: null,
  self_pay: null,
};

export function autoExpandForState(state: WorklistRowState): WorklistAutoExpand {
  return AUTO_EXPAND_BY_STATE[state];
}

/**
 * Row states that are eligible for the "Run check" bulk action — patient
 * has coverage on file but no eligibility result yet.
 */
export function isRunnableState(state: WorklistRowState): boolean {
  return state === 'awaiting_check';
}

/**
 * Row states that should NOT surface a calendar block badge by default
 * (queue-throughput states, not urgent-attention states). The badge can
 * still appear when the appointment is imminent — surfaces decide their
 * own threshold.
 */
export function isQuietState(state: WorklistRowState): boolean {
  return state === 'awaiting_check' || state === 'needs_coverage';
}
